import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { useCallback } from 'react';
import { useSaveSalesPortalCartFulfillmentMutator } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';

export interface UseSaveOrderFulfillmentSettingsOptions {
  onError?: () => void;
  onSuccess?: () => void;
}

export interface UseSaveOrderFulfillmentSettingsParams {
  saveSelectedFulfillment: (fulfillment: SavedSelectedFulfillment) => void;
}

/**
 * Hook to centralize fulfillment save orchestration for order entry.
 *
 * Orchestrates the process of saving fulfillment settings:
 * 1. Persist to backend via GraphQL mutation (errors handled internally with toast)
 * 2. Update local Zustand state (only if backend save succeeded)
 * 3. Execute optional success callback (e.g., refetch order summary)
 *
 * This ensures consumers cannot accidentally forget backend persistence,
 * which was a previous bug where checkout-panel only saved to local state.
 */
export const useSaveOrderFulfillmentSettings = ({
  saveSelectedFulfillment,
}: UseSaveOrderFulfillmentSettingsParams) => {
  const saveCartFulfillment = useSaveSalesPortalCartFulfillmentMutator();

  const saveFulfillment = useCallback(
    async (
      fulfillment: SavedSelectedFulfillment,
      options?: UseSaveOrderFulfillmentSettingsOptions,
    ) => {
      // 1. persist to backend (errors handled internally with toast)
      const success = await saveCartFulfillment(fulfillment);

      // only proceed if backend save succeeded
      if (success) {
        // 2. update local state
        saveSelectedFulfillment(fulfillment);

        // 3. optional callback (e.g., refetch order summary)
        options?.onSuccess?.();
        return;
      }

      options?.onError?.();
    },
    [saveCartFulfillment, saveSelectedFulfillment],
  );

  return { saveFulfillment };
};
